import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/4cf4c9f8-a16e-4360-be86-db2e72898751.png" 
              alt="Steetec Installatie B.V. Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-xl font-medium transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              Steetec Installatie B.V.
            </span>
          </div>
          <div className="flex gap-8">
            <a href="#home" className={`text-sm font-medium transition-colors hover:opacity-70 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Home</a>
            <a href="#products" className={`text-sm font-medium transition-colors hover:opacity-70 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Products</a>
            <a href="#about" className={`text-sm font-medium transition-colors hover:opacity-70 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>About</a>
            <a href="#contact" className={`text-sm font-medium transition-colors hover:opacity-70 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f3a] via-[#2d2550] to-[#4a3b6e]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-10 py-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex -space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 border-2 border-white"></div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-white"></div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 border-2 border-white"></div>
            </div>
            <p className="text-white/80 text-sm font-light">Trusted by 50,000+ users worldwide</p>
          </div>
          
          <div className="grid grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-7xl font-extrabold text-white leading-tight mb-8" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
                Modern iOS Applications for Enhanced Mobile Experience
              </h1>
            </div>
            
            <div className="flex justify-end">
              <p className="text-xl text-white/90 font-light leading-relaxed max-w-md">
                We specialize in creating secure, intuitive applications that protect and optimize your iOS devices with cutting-edge technology and user-centric design
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-6xl font-extrabold text-gray-900 mb-20 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Premium<br />iOS Solutions
          </h2>
          
          <div className="grid grid-cols-3 gap-10">
            {/* VPN Product */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer" data-product-shop>
              <div className="h-80 bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-shield-keyhole-line text-8xl text-blue-600"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>VPN</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Secure your online presence with military-grade encryption and lightning-fast connection speeds across global servers
                </p>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer" data-product-shop>
              <div className="h-80 bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-brush-3-line text-8xl text-emerald-600"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Cleaner</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Optimize your device performance by removing junk files, managing storage, and boosting speed with intelligent algorithms
                </p>
              </div>
            </div>

            {/* Security Suite Product */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer" data-product-shop>
              <div className="h-80 bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-lock-password-line text-8xl text-purple-600"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Security Suite</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Comprehensive protection against threats with real-time monitoring, malware detection, and privacy safeguards
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative overflow-hidden">
        <div className="grid grid-cols-5">
          <div className="col-span-2 bg-white py-24 px-16 flex items-center">
            <h2 className="text-6xl font-bold text-gray-900 leading-tight">
              Expertise in iOS<br />Development Since<br />2020
            </h2>
          </div>
          
          <div className="col-span-3 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 relative min-h-[600px]">
            <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-br from-pink-300/60 to-pink-500/60 rounded-[60px] blur-sm"></div>
            <div className="absolute top-40 right-32 w-48 h-48 bg-gradient-to-br from-purple-300/70 to-purple-500/70 rounded-[50px] blur-sm"></div>
            <div className="absolute bottom-32 left-40 w-56 h-56 bg-gradient-to-br from-blue-300/60 to-blue-500/60 rounded-[70px] blur-sm"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-br from-pink-400/80 to-pink-600/80 rounded-[45px] blur-sm"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-br from-purple-400/50 to-purple-600/50 rounded-[80px] blur-sm"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-10 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">Why Choose Us</p>
            <h2 className="text-7xl font-extrabold text-gray-900 leading-tight">
              Security & Performance<br />Built Into Every App
            </h2>
          </div>
          
          <div className="grid grid-cols-3 gap-8">
            <div className="bg-gray-100 rounded-xl p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Advanced Security</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Military-grade encryption and multi-layer protection ensure your data remains private and secure at all times
              </p>
            </div>
            
            <div className="bg-gray-100 rounded-xl p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-speed-up-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Lightning Fast</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Optimized performance algorithms deliver instant results without compromising your device battery or resources
              </p>
            </div>
            
            <div className="bg-gray-100 rounded-xl p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-user-heart-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">User-Centric Design</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Intuitive interfaces designed with user experience in mind make complex features accessible to everyone
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#0a0a0a] rounded-3xl overflow-hidden">
            <div className="grid grid-cols-5">
              <div className="col-span-2 relative">
                <div className="absolute inset-0 m-8 rounded-2xl overflow-hidden">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/77713f12008c85dc4b125a2f6195147f.jpg"
                    alt="Customer testimonial"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              
              <div className="col-span-3 p-20 flex flex-col justify-center">
                <div className="text-7xl text-[#f5e6d3] mb-8 font-serif">"</div>
                <p className="text-3xl text-white font-light leading-relaxed mb-12">
                  The VPN app has completely transformed how I browse online. Fast, secure, and incredibly easy to use. I feel protected wherever I go.
                </p>
                <div>
                  <p className="text-lg text-white font-medium mb-2">Sarah Mitchell</p>
                  <p className="text-sm text-white/70 font-light">Digital Marketing Manager</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[700px] overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=modern%20sleek%20iPhone%20devices%20displaying%20security%20and%20cleaning%20applications%20on%20screen%20minimalist%20setup%20with%20soft%20lighting%20technology%20showcase%20professional%20product%20photography%20clean%20white%20background%20premium%20aesthetic%20high%20resolution&width=1920&height=700&seq=cta-background-001&orientation=landscape"
          alt="iOS devices showcase"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35"></div>
        
        <div className="relative z-10 h-full max-w-7xl mx-auto px-10 flex flex-col justify-between py-32">
          <h2 className="text-8xl font-bold text-white leading-tight lowercase max-w-2xl" style={{ textShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
            experience the future<br />of mobile security
          </h2>
          
          <div className="flex justify-end">
            <p className="text-xl text-white font-light leading-relaxed max-w-md text-right">
              Join thousands of users who trust our applications to keep their devices secure, clean, and performing at their best every single day
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-black mx-10 mb-10 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-16 pt-20 pb-8">
          <div className="grid grid-cols-10 gap-16 pb-16">
            <div className="col-span-4">
              <h3 className="text-5xl font-light text-white mb-8">Stay Updated</h3>
              <p className="text-base text-gray-400 leading-relaxed mb-12">
                Subscribe to our newsletter for the latest updates on new features, security tips, and exclusive offers for our iOS applications
              </p>
              <div className="border-b border-gray-700 pb-2">
                <input 
                  type="email" 
                  placeholder="Enter your email address" 
                  className="w-full bg-transparent text-white text-sm placeholder-gray-600 outline-none"
                />
              </div>
            </div>
            
            <div className="col-span-3">
              <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-6">Navigation</h4>
              <div className="space-y-4">
                <a href="#home" className="block text-base text-white hover:text-gray-300 transition-colors">Home</a>
                <a href="#products" className="block text-base text-white hover:text-gray-300 transition-colors">Products</a>
                <a href="#about" className="block text-base text-white hover:text-gray-300 transition-colors">About</a>
                <a href="#contact" className="block text-base text-white hover:text-gray-300 transition-colors">Contact</a>
              </div>
            </div>
            
            <div className="col-span-3">
              <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-6">Contact</h4>
              <div className="space-y-3 mb-12">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-white w-5 h-5 flex items-center justify-center"></i>
                  <a href="tel:+12406798029" className="text-base text-white hover:text-gray-300 transition-colors">+1 2406798029</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-white w-5 h-5 flex items-center justify-center"></i>
                  <a href="mailto:help@steetecinstallatiebv.com" className="text-base text-white hover:text-gray-300 transition-colors">help@steetecinstallatiebv.com</a>
                </div>
              </div>
              
              <h4 className="text-sm uppercase tracking-wider text-gray-500 mb-6">Location</h4>
              <div className="space-y-2 leading-relaxed">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-white w-5 h-5 flex items-center justify-center mt-1"></i>
                  <div className="text-base text-white">
                    <p>Veilingweg 33</p>
                    <p>Honselersdijk, 2675 BR</p>
                    <p>Netherlands</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex items-center justify-between">
            <div className="flex gap-8">
              <a href="https://linkedin.com" className="text-base text-white hover:text-gray-300 transition-colors">LinkedIn</a>
              <a href="https://twitter.com" className="text-base text-white hover:text-gray-300 transition-colors">Twitter</a>
            </div>
            
            <p className="text-sm text-gray-600">© 2025 Steetec Installatie B.V. All rights reserved.</p>
            
            <div className="flex gap-8">
              <a href="https://readdy.ai/?ref=logo" className="text-base text-white hover:text-gray-300 transition-colors whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
